import { actionsKeys, capitalize, getRequiredFields, permissionActions } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  useGetDocumentTypeDropdown,
  useGetGlMasterData,
  useGetJournalEntryById,
} from "@/services";
import {
  AMOUNT,
  ATTACHMENT_FILE,
  BILLING_MONTH,
  BILLING_MONTH_YEAR,
  BILLING_YEAR,
  CHEQUE_NO,
  CLEARING,
  COMBINE,
  COMMENT,
  CUSTOMER_ACC,
  DATE,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  GL_ACCOUNT,
  JOURNAL_ENTRY_FILE,
  JOURNAL_ENTRY_ID,
  LINE_ITEM_TEXT,
  LINE_ITEMS,
  OL_REF,
  OL_TOTAL,
  PAYMENT_METHOD,
  POSTING_DATE,
  POSTING_MONTH,
  SUB_LEDGER,
  VENDOR_ACC,
  YEAR,
} from "./consts";
import {
  useAddJournalEntryMutation,
  useAddPostJournalEntryMutation,
} from "@/services";
import moment from "moment";

const useAddEdit = () => {
  const { billingId, action } = useParams();
  const navigate = useNavigate();
  const attachmentRef = useRef(null);
  const journalEntryRef = useRef(null);
  const isView = action == "view";

  const [keys, setKeys] = useState({
    accountName: Math.random() * 2,
    documentTypes: Math.random() * 2,
    paymentTerm: Math.random() * 2,
  });

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const form = useForm<any>({
    defaultValues: {
      [JOURNAL_ENTRY_ID]: "",
      [DOCUMENT_TYPE]: "",
      [BILLING_MONTH_YEAR]: null,
      [COMMENT]: "",
      [ATTACHMENT_FILE]: null,
      [JOURNAL_ENTRY_FILE]: null,
      [POSTING_DATE]: "",
      [POSTING_MONTH]: "",
      [YEAR]: "",
      [LINE_ITEMS]: [],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbData = useMemo(() => {
    return [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.journalEntry}`,
        name: <span className="text-primary">{"Unified Journal Entry"}</span>,
      },
      {
        link: "",
        name: <span className="text-primary">{capitalize(action || "")}</span>,
      },
    ];
  }, []);

  const breadcrumbTitle = `Unified Journal Entry - ${communityDetail?.name}`;

  const onBackClick = () => {
    navigate(
      `/${routes.cam}/${routes.activityDashboard}/${routes.journalEntry}`
    );
  };

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: journalEntryData, isLoading: isJournalEntryLoading } =
    useGetJournalEntryById({
      id: billingId as string,
      queryParams: {
        enabled: !!billingId,
      },
    });

  const hasPostPermission = useMemo(() => accessPermission?.[actionsKeys?.UNIFIED_JE?.toLowerCase()]?.[permissionActions.POST], [accessPermission]);

  useEffect(() => {
    if (journalEntryData) {
      form.reset({
        [JOURNAL_ENTRY_ID]:
          journalEntryData?.data?.[JOURNAL_ENTRY_ID]?.toString() || "",
        [DOCUMENT_ID]: journalEntryData?.data?.[DOCUMENT_ID]?.toString() || "",
        [BILLING_MONTH_YEAR]:
          new Date(
            `${journalEntryData?.data?.billingMonth?.toString()}/01/${journalEntryData?.data?.billingYear?.toString()}`
          ) || null,
        [COMMENT]: journalEntryData?.data?.[COMMENT]?.toString() || "",
        [ATTACHMENT_FILE]: {
          name:
            journalEntryData?.data?.attachmentOriginalFileName?.toString() ||
            undefined,
        },
        [JOURNAL_ENTRY_FILE]: {
          name:
            journalEntryData?.data?.journalEntryOriginalFileName?.toString() ||
            undefined,
        },
        [POSTING_DATE]:
          journalEntryData?.data?.[POSTING_DATE]?.toString() || "",
        [POSTING_MONTH]:
          journalEntryData?.data?.[POSTING_MONTH]?.toString() || "",
        [YEAR]: journalEntryData?.data?.[YEAR]?.toString() || "",
        [LINE_ITEMS]: journalEntryData?.data?.[LINE_ITEMS] || [],
      });
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 3 }));
    }
  }, [journalEntryData]);

  useEffect(() => {
    if (documentTypes) {
      const documentTypeId = documentTypes
        ?.find((data: any) => {
          return data?.documentType?.toLowerCase() == "je";
        })
        ?.documentTypeId?.toString();
      if (documentTypeId) {
        form.setValue(DOCUMENT_TYPE, documentTypeId);
      } else {
        toast.error(
          "The Document Type 'JE' was not found. Kindly add it to proceed."
        );
        onBackClick();
      }
      setKeys((prev: any) => ({ ...prev, documentTypes: Math.random() * 4 }));
    }
  }, [documentTypes]);

  const {
    mutate: addInfo,
    isPending: isAddPending,
    isError: isAddJournalError,
  } = useAddJournalEntryMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data: any) => {
      toast.success("Line items fetched Successfully");

      form.setValue(JOURNAL_ENTRY_ID, data?.data?.data?.[JOURNAL_ENTRY_ID]);
      const lineItems = data?.data?.data?.[LINE_ITEMS]?.map((item: any) => ({
        ...item,
        [DATE]: item?.[DATE]?.toString() || null,
        [LINE_ITEM_TEXT]: item?.[LINE_ITEM_TEXT]?.toString() || "",
        [CHEQUE_NO]: item?.[CHEQUE_NO]?.toString() || "",
        [AMOUNT]: item?.[AMOUNT]?.toString() || "",
        [DRCR]: item?.[DRCR]?.toString() || "",
        [GL_ACCOUNT]: item?.[GL_ACCOUNT]?.toString() || "",
        [SUB_LEDGER]: !!item?.[SUB_LEDGER],
        [COMBINE]: !!item?.[COMBINE],
        [CUSTOMER_ACC]: item?.[CUSTOMER_ACC]?.toString() || "",
        [VENDOR_ACC]: item?.[VENDOR_ACC]?.toString() || "",
        [PAYMENT_METHOD]: item?.[PAYMENT_METHOD]?.toString() || "",
        [CLEARING]: item?.[CLEARING]?.toString() || "",
        [OL_REF]: item?.[OL_REF]?.toString() || "",
        [OL_TOTAL]: item?.[OL_TOTAL]?.toString() || "",
      }));
      form.setValue(LINE_ITEMS, lineItems);
    },
  });

  const { mutate: postInfo, isPending: isPostPending } =
    useAddPostJournalEntryMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Journal Posted Successfully");
        onBackClick();
      },
    });

  const onSubmit = (data: any) => {
    if (form.watch(LINE_ITEMS)?.length) {
      postInfo({
        documentId: form.watch(JOURNAL_ENTRY_ID),
      });
    } else {
      if (!data?.[BILLING_MONTH_YEAR]) {
        form.setError(BILLING_MONTH_YEAR, {
          message: "This field is required",
        });
        return;
      }
      if (!data?.[JOURNAL_ENTRY_FILE]) {
        form.setError(JOURNAL_ENTRY_FILE, {
          message: "This field is required",
        });
        return;
      }
      if (!data?.[ATTACHMENT_FILE]) {
        form.setError(ATTACHMENT_FILE, {
          message: "This field is required",
        });
        return;
      }
      if (!data?.[POSTING_DATE]) {
        form.setError(POSTING_DATE, {
          message: "This field is required",
        });
        return;
      }

      delete data[LINE_ITEMS];

      addInfo({
        data: {
          ...data,
          communityId: communityDetail?.communityId,
          [BILLING_MONTH]: data?.[BILLING_MONTH_YEAR]
            ? moment(data?.[BILLING_MONTH_YEAR]).format("MM")
            : "",
          [BILLING_YEAR]: data?.[BILLING_MONTH_YEAR]
            ? moment(data?.[BILLING_MONTH_YEAR]).format("YYYY")
            : "",
          [POSTING_DATE]: moment(data?.[POSTING_DATE]).format("YYYY-MM-DD"),
        },
      });
    }
  };

  const lineItems = useWatch({ control: form.control, name: LINE_ITEMS });

  const balanceAmount = useMemo(() => {
    if (Array.isArray(lineItems)) {
      const allValues = lineItems
        .filter((item) => item[AMOUNT] && item[GL_ACCOUNT] != "1001")
        .reduce((sum, item) => sum + Number(item[AMOUNT] || 0), 0);

      const isFinalEntry = lineItems
        .filter((item) => item[AMOUNT] && item[GL_ACCOUNT] == "1001")
        .reduce((sum, item) => sum + Number(item[AMOUNT] || 0), 0);

      return isFinalEntry - allValues;
    }
    return 0;
  }, [lineItems]);

  return {
    breadcrumbData,
    onBackClick,
    communityDetail,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    documentTypes,
    isLoadingGlMaster,
    onCancel: onBackClick,
    breadcrumbTitle,
    keys,
    onSubmit,
    attachmentRef,
    isView,
    isAddPending,
    isAddJournalError,
    journalEntryRef,
    balanceAmount,
    isPostPending,
    isJournalEntryLoading,
    hasPostPermission
  };
};

export default useAddEdit;
